import { useState, useMemo, useCallback, useEffect } from "react";
import { range, compose, first, last } from "lodash/fp";
import { startOfMonth, startOfWeek, addWeeks, max, min, isBefore, isAfter, addMonths, subMonths, addYears, subYears, subWeeks, getWeeksInMonth, getWeekOfMonth, isSameWeek } from "date-fns/fp";
function useWeekPicker({ initialValue, maxWeek, minWeek }) {
  const [monthInView, setMonthInView] = useState(
    startOfMonth(initialValue || /* @__PURE__ */ new Date())
  );
  const [highlightedDate, setHighlightedDate] = useState();
  const weeksInView = useMemo(() => {
    const startDate = startOfWeek(monthInView);
    return range(0, 6).map((d) => addWeeks(d, startDate));
  }, [monthInView]);
  const clampLowerMonth = useCallback(
    (date) => minWeek ? max([startOfMonth(minWeek), date]) : date,
    [minWeek]
  );
  const clampUpperMonth = useCallback(
    (date) => maxWeek ? min([startOfMonth(maxWeek), date]) : date,
    [maxWeek]
  );
  const handleHighlightedDateChange = useCallback(
    (changeFunc, clamp = false) => setHighlightedDate((date) => {
      if (!date) {
        return startOfWeek(monthInView || /* @__PURE__ */ new Date());
      }
      const newDate = changeFunc(date);
      if (isBefore(minWeek, newDate)) {
        return clamp ? minWeek : date;
      }
      if (isAfter(maxWeek, newDate)) {
        return clamp ? maxWeek : date;
      }
      return newDate;
    }),
    [maxWeek, minWeek, monthInView]
  );
  const viewNextMonth = useCallback(() => {
    setHighlightedDate(void 0);
    setMonthInView(compose(clampUpperMonth, addMonths(1)));
  }, [clampUpperMonth]);
  const viewPreviousMonth = useCallback(() => {
    setHighlightedDate(void 0);
    setMonthInView(compose(clampLowerMonth, subMonths(1)));
  }, [clampLowerMonth]);
  const viewNextYear = useCallback(() => {
    setHighlightedDate(void 0);
    setMonthInView(compose(clampUpperMonth, addYears(1)));
  }, [clampUpperMonth]);
  const viewPreviousYear = useCallback(() => {
    setHighlightedDate(void 0);
    setMonthInView(compose(clampLowerMonth, subYears(1)));
  }, [clampLowerMonth]);
  const highlightNextWeek = useCallback(() => {
    handleHighlightedDateChange(addWeeks(1));
  }, [handleHighlightedDateChange]);
  const highlightPreviousWeek = useCallback(() => {
    handleHighlightedDateChange(subWeeks(1));
  }, [handleHighlightedDateChange]);
  const highlightStartOfMonth = useCallback(() => {
    setHighlightedDate(first(weeksInView));
  }, [weeksInView]);
  const highlightEndOfMonth = useCallback(() => {
    setHighlightedDate(last(weeksInView));
  }, [weeksInView]);
  const highlightNextMonth = useCallback(() => {
    const nextMonth = addMonths(1, monthInView);
    const maxWeeks = getWeeksInMonth(nextMonth);
    setMonthInView(nextMonth);
    handleHighlightedDateChange(
      (d) => addWeeks(
        Math.min(getWeekOfMonth(max([d, monthInView])), maxWeeks) - 1,
        startOfWeek(nextMonth)
      ),
      true
    );
  }, [handleHighlightedDateChange, monthInView]);
  const highlightPreviousMonth = useCallback(() => {
    const prevMonth = subMonths(1, monthInView);
    const maxWeeks = getWeeksInMonth(prevMonth);
    setMonthInView(prevMonth);
    handleHighlightedDateChange(
      (d) => addWeeks(
        Math.min(getWeekOfMonth(max([d, monthInView])), maxWeeks) - 1,
        startOfWeek(prevMonth)
      ),
      true
    );
  }, [handleHighlightedDateChange, monthInView]);
  const highlightNextYear = useCallback(() => {
    const nextYear = addYears(1, monthInView);
    const maxWeeks = getWeeksInMonth(nextYear);
    setMonthInView(nextYear);
    handleHighlightedDateChange(
      (d) => addWeeks(
        Math.min(getWeekOfMonth(max([d, monthInView])), maxWeeks) - 1,
        startOfWeek(nextYear)
      ),
      true
    );
  }, [handleHighlightedDateChange, monthInView]);
  const highlightPreviousYear = useCallback(() => {
    const prevYear = subYears(1, monthInView);
    const maxWeeks = getWeeksInMonth(prevYear);
    setMonthInView(prevYear);
    handleHighlightedDateChange(
      (d) => addWeeks(
        Math.min(getWeekOfMonth(max([d, monthInView])), maxWeeks) - 1,
        startOfWeek(prevYear)
      ),
      true
    );
  }, [handleHighlightedDateChange, monthInView]);
  useEffect(() => {
    if (highlightedDate && !weeksInView.some(isSameWeek(highlightedDate))) {
      setMonthInView(startOfMonth(highlightedDate));
    }
  }, [highlightedDate, weeksInView]);
  return {
    highlightEndOfMonth,
    highlightNextMonth,
    highlightNextWeek,
    highlightNextYear,
    highlightPreviousMonth,
    highlightPreviousWeek,
    highlightPreviousYear,
    highlightStartOfMonth,
    highlightedDate,
    monthInView,
    setHighlightedDate,
    viewNextMonth,
    viewNextYear,
    viewPreviousMonth,
    viewPreviousYear,
    weeksInView
  };
}
export {
  useWeekPicker
};
